import { LoggerName, getLogger } from "~/modules/logger";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { CbmImpl, ParticipantType } from "~/modules/cbm";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { Worker } from "~/packages/taskrouter/Worker";
import { Reservation } from "~/packages/taskrouter";
import { TaskParticipant } from "~/modules/participant";
import {
    getReservationsByTask,
    canHold,
    checkWorker,
    checkReservationExists,
    handleGetParticipantsByTask,
    checkParticipantExists
} from "~/modules/actions/ActionUtils";
import { sendWorkerTrackingEvent, toCauseMessage } from "~/modules/telemetry/utils";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { Action } from "~/modules/actions/Action";
import { throwFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";

interface InitContext {
    context: ContextManager;
    taskRouter: TaskRouterImpl;
    logger: ReturnType<ReturnType<typeof getLogger>>;
    worker: Worker;
    analytics: AnalyticsImpl;
    cbm: CbmImpl;
}


































export class UnholdVoiceParticipant implements Action<Promise<void>> {
    


    readonly #targetParticipantSid: string;

    


    readonly #taskSid: string;

    constructor(targetParticipantSid: string, taskSid: string) {
        this.#targetParticipantSid = targetParticipantSid;
        this.#taskSid = taskSid;
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const initResult = await this.#initializeContext(context);
        const validReservation = this.#validateReservationAndWorker(initResult);
        const participant = await this.#findAndValidateParticipant(initResult, validReservation);
        await this.#executeUnhold(initResult, validReservation, participant);
    }

    async #initializeContext(context: ContextManager) {
        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const logger = getLogger(context)(LoggerName.Actions);
        const worker = taskRouter.worker;
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();

        logger.debug(`unholdVoiceParticipant invoked with taskSid: ${this.#taskSid}`);

        return { context, taskRouter, logger, worker, analytics, cbm };
    }

    #validateReservationAndWorker(initResult: InitContext) {
        const { worker, logger, analytics, context } = initResult;

        checkWorker(worker, {
            actionName: "UnholdVoiceParticipant",
            moduleName: "actions/Voice",
            logger,
            analytics,
            context,
            event: EVENTS.UnholdVoiceParticipantCompleted,
            extra: {
                taskSid: this.#taskSid,
                targetParticipantSid: this.#targetParticipantSid
            },
            worker
        });

        const reservations = getReservationsByTask(worker, this.#taskSid);
        const reservation = reservations.find((res) => res.status === "accepted");

        checkReservationExists(reservation, this.#taskSid, {
            actionName: "UnholdVoiceParticipant",
            moduleName: "actions/Voice",
            logger,
            analytics,
            context,
            event: EVENTS.UnholdVoiceParticipantCompleted,
            extra: {
                taskSid: this.#taskSid,
                targetParticipantSid: this.#targetParticipantSid
            },
            worker
        });

        const validReservation = reservation!;

        if (!canHold(validReservation, worker)) {
            const errorMsg =
                `UnholdVoiceParticipant: Cannot unhold reservation ${validReservation.sid}` +
                ` because it is not an active call assigned to the worker`;
            logger.error(errorMsg);
            sendWorkerTrackingEvent(EVENTS.UnholdVoiceParticipantCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                targetParticipantSid: this.#targetParticipantSid
            });
            return throwFlexSdkError(context)(
                ErrorCode.WorkerCannotUnholdReservation,
                { severity: ErrorSeverity.Error },
                errorMsg
            );
        }

        return validReservation;
    }

    async #findAndValidateParticipant(initResult: InitContext, validReservation: Reservation) {
        const { cbm, logger, analytics, context, worker } = initResult;
        const task = validReservation.task;

        const participants = await handleGetParticipantsByTask(cbm, task, {
            actionName: "UnholdVoiceParticipant",
            moduleName: "actions/Voice",
            logger,
            analytics,
            context,
            event: EVENTS.UnholdVoiceParticipantCompleted,
            extra: {
                taskSid: this.#taskSid,
                reservationSid: validReservation.sid,
                targetParticipantSid: this.#targetParticipantSid
            },
            worker
        });

        const participant = participants.find(
            (p) =>
                p.participantSid === this.#targetParticipantSid ||
                p.routingProperties?.workerSid === this.#targetParticipantSid
        );

        checkParticipantExists(participant, this.#targetParticipantSid, this.#taskSid, {
            actionName: "UnholdVoiceParticipant",
            moduleName: "actions/Voice",
            logger,
            analytics,
            context,
            event: EVENTS.UnholdVoiceParticipantCompleted,
            extra: {
                taskSid: this.#taskSid,
                reservationSid: validReservation.sid,
                targetParticipantSid: this.#targetParticipantSid
            },
            worker
        });

        return participant!; 
    }

    async #executeUnhold(initResult: InitContext, validReservation: Reservation, participant: TaskParticipant) {
        if (participant?.type === ParticipantType.External) {
            await this.#unholdExternalParticipant(initResult, validReservation, participant);
        } else if (
            participant?.routingProperties?.workerSid &&
            (participant.type === ParticipantType.Supervisor || participant.type === ParticipantType.Agent)
        ) {
            await this.#unholdWorkerParticipant(initResult, validReservation, participant);
        } else {
            await this.#unholdGeneralParticipant(initResult, validReservation, participant);
        }
    }

    async #unholdExternalParticipant(
        initResult: InitContext,
        validReservation: Reservation,
        participant: TaskParticipant
    ) {
        const { cbm, logger, analytics, context, worker } = initResult;
        const task = validReservation.task;

        try {
            await cbm.unholdParticipant(task, this.#targetParticipantSid);
            sendWorkerTrackingEvent(EVENTS.UnholdVoiceParticipantCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                reservationSid: validReservation.sid,
                targetParticipantSid: this.#targetParticipantSid,
                participantSid: participant.participantSid,
                participantType: participant.type
            });
            return Promise.resolve();
        } catch (error: unknown) {
            const errorMessage = toCauseMessage(error);
            const errorObject = error;

            const errorMsg = `UnholdVoiceParticipant: Failed to unhold external participant ${
                this.#targetParticipantSid
            }`;
            logger.error(errorMsg, errorObject);
            sendWorkerTrackingEvent(EVENTS.UnholdVoiceParticipantCompleted, analytics, worker, {
                error: `${errorMsg} (${errorMessage})`,
                taskSid: this.#taskSid,
                reservationSid: validReservation.sid,
                targetParticipantSid: this.#targetParticipantSid,
                participantSid: participant.participantSid,
                participantType: participant.type
            });
            return throwFlexSdkError(context)(
                ErrorCode.FailedToUnholdExternalParticipant,
                { severity: ErrorSeverity.Error },
                errorMsg,
                errorObject
            );
        }
    }

    async #unholdWorkerParticipant(
        initResult: InitContext,
        validReservation: Reservation,
        participant: TaskParticipant
    ) {
        const { logger, analytics, context, worker } = initResult;
        const task = validReservation.task;

        const workerSid = participant.routingProperties?.workerSid;
        if (!workerSid) {
            throw new Error(`Worker participant missing workerSid for participant ${participant.participantSid}`);
        }

        try {
            await task.hold(workerSid, false);
            sendWorkerTrackingEvent(EVENTS.UnholdVoiceParticipantCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                reservationSid: validReservation.sid,
                targetParticipantSid: this.#targetParticipantSid,
                participantSid: participant.participantSid,
                participantType: participant.type
            });
            return Promise.resolve();
        } catch (error: unknown) {
            const errorMessage = toCauseMessage(error);
            const errorObject = error;

            const errorMsg = `UnholdVoiceParticipant: Failed to unhold worker participant ${
                this.#targetParticipantSid
            }`;
            logger.error(errorMsg, errorObject);
            sendWorkerTrackingEvent(EVENTS.UnholdVoiceParticipantCompleted, analytics, worker, {
                error: `${errorMsg} (${errorMessage})`,
                taskSid: this.#taskSid,
                reservationSid: validReservation.sid,
                targetParticipantSid: this.#targetParticipantSid,
                participantSid: participant.participantSid,
                participantType: participant.type
            });
            return throwFlexSdkError(context)(
                ErrorCode.FailedToUnholdWorkerParticipant,
                { severity: ErrorSeverity.Error },
                errorMsg,
                errorObject
            );
        }
    }

    async #unholdGeneralParticipant(
        initResult: InitContext,
        validReservation: Reservation,
        participant: TaskParticipant
    ) {
        const { logger, analytics, context, worker } = initResult;
        const task = validReservation.task;

        try {
            await task.updateParticipant({
                hold: false
            });
            sendWorkerTrackingEvent(EVENTS.UnholdVoiceParticipantCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                reservationSid: validReservation.sid,
                targetParticipantSid: this.#targetParticipantSid,
                participantSid: participant?.participantSid,
                participantType: participant?.type
            });
            return Promise.resolve();
        } catch (error: unknown) {
            const errorMessage = toCauseMessage(error);
            const errorObject = error;

            const errorMsg = `UnholdVoiceParticipant: Failed to unhold participant ${this.#targetParticipantSid}`;
            logger.error(errorMsg, errorObject);
            sendWorkerTrackingEvent(EVENTS.UnholdVoiceParticipantCompleted, analytics, worker, {
                error: `${errorMsg} (${errorMessage})`,
                taskSid: this.#taskSid,
                reservationSid: validReservation.sid,
                targetParticipantSid: this.#targetParticipantSid,
                participantSid: participant?.participantSid,
                participantType: participant?.type
            });
            return throwFlexSdkError(context)(
                ErrorCode.FailedToUnholdVoiceParticipant,
                { severity: ErrorSeverity.Error },
                errorMsg,
                errorObject
            );
        }
    }
}
